import React, { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getHours } from "date-fns";
import {
  Box,
  Button,
  Divider,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import styled from "@emotion/styled";
import AddIcon from "@icons/myicons/plus.svg?react";
import LockIcon from "@icons/myicons/lock.svg?react";
import { useMyself } from "@api/v1/hooks/useMyself";
import { PageLayout } from "@components";
import { intervalToTimeRange } from "@helpers";
import { DepotsSelector } from "@features/selectDepots/DepotsSelector";
import { useDashboardSelectedGroupsContext } from "@features/dashboard/DashboardSelectedGroupProvider";
import { DashboardChartContainer } from "@features/dashboard/components/Dashboard/DashboardChartContainer";
import { AddOptionsList } from "@features/add/AddPortfolioMenu";
import { AddPortfolio, AddPortfolioHandle } from "@features/add/AddPortfolio";
import { PortfolioLimitGate } from "@features/add/PortfolioLimitGate";
import { ImportType } from "@features/add/components/types";
import { useAppLayout } from "@providers";
import { useInvestmentsChartDataTotal } from "../../hooks/chart/useInvestmentsChartDataTotal";
import { useInvestmentsCount } from "./hooks";
import {
  Assets,
  DashboardNotificationManager,
  DashboardPerformance,
} from "./components";
import { getWelcomePhrase } from "./utils";
import { DashboardSidebarSection } from "./components/DashboardSidebarSection";

const Container = styled(Box)(({ theme }) => ({
  minHeight: 620,
  display: "flex",
  marginTop: theme.spacing(4),
}));

const MetricsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
  minWidth: 366,

  [theme.breakpoints.down("md")]: {
    flexDirection: "column-reverse",
    gap: theme.spacing(5),
    width: "100%",
    minWidth: "unset",
  },

  "& > :not(style)": {
    flex: 1,
  },
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
  borderColor: theme.palette.border.secondary,
}));

export const DashboardPage = () => {
  const { t } = useTranslation();
  const { sidebarCollapsed } = useAppLayout();
  const theme = useTheme();
  const isLessMd = useMediaQuery(theme.breakpoints.down("md"));
  const isLess1200 = useMediaQuery(theme.breakpoints.down(1200));
  const isLess1400 = useMediaQuery(theme.breakpoints.down(1450));
  const isXs = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const showCommunityBlockOnMobile = sidebarCollapsed ? isLess1200 : isLess1400;
  const { startDate, endDate } = intervalToTimeRange("oneYear");
  const addPortfolioRef = useRef<AddPortfolioHandle | null>(null);
  const [menuEl, setMenuEl] = useState<HTMLElement | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const { selectedGroup, accounts, groupedAccountIds, refetch, isFetching } =
    useDashboardSelectedGroupsContext();

  const accountIds = useMemo(
    () => [
      ...groupedAccountIds.cashIds,
      ...groupedAccountIds.creditCardIds,
      ...groupedAccountIds.depotIds,
    ],
    [groupedAccountIds]
  );

  const { statistic, isFetching: isStatisticLoading } =
    useInvestmentsChartDataTotal(accountIds, startDate!, endDate!);

  const investmentsCount = useInvestmentsCount(
    accounts,
    groupedAccountIds.depotIds
  );

  const { data: myselfData } = useMyself();

  const [hasAutoOpenedModal, setHasAutoOpenedModal] = useState(false);
  if (!hasAutoOpenedModal && !isFetching && accountIds.length === 0) {
    setHasAutoOpenedModal(true);
    setIsModalOpen(true);
  }

  const totalValue = statistic?.portfolioValue ?? 0;
  const name = myselfData?.profileReference?.firstName;
  const welcomePhrase = getWelcomePhrase(getHours(new Date()));
  const welcomeText = name ? `${welcomePhrase}, ${name}` : welcomePhrase;

  const onSelect = useCallback((val: ImportType) => {
    addPortfolioRef.current?.onSelect(val);
  }, []);

  const handleOpenMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuEl(event.currentTarget);
  };

  return (
    <PageLayout
      isMainPage
      hasAddButton
      mobileMenu={<AddOptionsList onSelect={onSelect} />}
      title={
        <Box>
          <Typography variant="body4" color="textSecondary">
            {welcomeText}
          </Typography>
          <Typography variant="h4" component="h1" mb={theme.spacing(2)}>
            {t("dashboardPage.header.title")}
          </Typography>
          <DepotsSelector
            selectedGroup={selectedGroup}
            selectedGroupParams={{ id: "dashboard" }}
            color="text.secondary"
            onSave={() => refetch()}
          />
        </Box>
      }
      actionButton={
        <PortfolioLimitGate
          renderLocked={
            <Button
              variant="tertiary"
              startIcon={<LockIcon width="16px" height="16px" />}
              sx={{ cursor: "not-allowed", width: isXs ? "100%" : "auto" }}
            >
              {t("dashboardPage.dashboard.header.add")}
            </Button>
          }
        >
          <Button
            variant="primary"
            startIcon={<AddIcon width="18px" height="12px" />}
            data-testid="add-account"
            onClick={handleOpenMenuClick}
            sx={{
              width: isXs ? "100%" : "auto",
            }}
          >
            {t("dashboardPage.dashboard.header.add")}
          </Button>
        </PortfolioLimitGate>
      }
    >
      <DashboardNotificationManager />

      <Container
        flexDirection={
          isLessMd || (isLess1200 && !sidebarCollapsed) ? "column" : "row"
        }
        gap={isLessMd || (isLess1200 && !sidebarCollapsed) ? 4 : 5}
      >
        <DashboardChartContainer
          currency="EUR"
          totalValue={totalValue ?? 0}
          statistic={statistic}
          isStatisticsLoading={isStatisticLoading}
          showCommunity={!showCommunityBlockOnMobile}
        />
        <MetricsContainer>
          <DashboardPerformance
            showCompare
            showDividendsToggle
            showCapitalToggle
            chartHeight={356}
          />
        </MetricsContainer>
        {isLessMd && <StyledDivider />}
      </Container>

      <Assets
        investmentsCount={investmentsCount}
        cashAccountsBalance={statistic?.cashBalance ?? 0}
      />

      {showCommunityBlockOnMobile && <DashboardSidebarSection topPadding={5} />}
      <AddPortfolio
        ref={addPortfolioRef}
        menuEl={menuEl}
        setMenuEl={setMenuEl}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
      />
    </PageLayout>
  );
};
